<template>
  <div class="ordemservico-filter-container bg-[var(--fundoForm)] p-4 rounded-lg shadow-lg">
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-[var(--text)] font-semibold">Filtros da Ordem de Serviço</h3>
      <div class="flex items-center gap-3">
        <button @click="clearFilters" class="text-blue-400 hover:text-blue-300 text-sm underline">
          Limpar Filtros
        </button>
        <button @click="closeFilter" class="text-gray-400 hover:text-[var(--text)] text-lg transition-colors">
          <i class="fa-solid fa-times"></i>
        </button>
      </div>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">

      <div class="filter-group">
        <label class="filter-label">Data Início</label>
        <multiselect
          v-model="selectedDataInicialPreset"
          :options="datePresets"
          track-by="value"
          label="label"
          placeholder="Selecione..."
          :searchable="false"
          @select="handleDataPresetSelect"
        />
      </div>

      <div v-if="isCustomDate" class="filter-group col-span-2">
        <label class="filter-label">Data Inicial (Intervalo Personalizado)</label>
        <DatePicker
          v-model.range="dataInicialRange"
          @update:model-value="onDataInicialRangeChange"
          :is-dark="true"
          mode="date"
          :masks="dateMasks"
          locale="pt"
        >
          <template #default="{ togglePopover }">
            <div @click="togglePopover" class="filter-input cursor-pointer flex items-center justify-between">
              <span v-if="dataInicialRange.start && dataInicialRange.end" class="text-[var(--text)] truncate">
                {{ formatDisplayDate(dataInicialRange.start) }} - {{ formatDisplayDate(dataInicialRange.end) }}
              </span>
              <span v-else class="text-gray-400 truncate">Selecione o intervalo de datas...</span>
            </div>
          </template>
        </DatePicker>
      </div>

      <div class="filter-group">
        <label class="filter-label">Cliente</label>
        <multiselect
          v-model="selectedCliente"
          :options="clienteOptionsWithAll"
          track-by="value"
          label="label"
          placeholder="Todos os clientes"
          @select="onClienteSelect"
        />
      </div>

      <div class="filter-group">
        <label class="filter-label">Sub-Cliente</label>
        <multiselect
          v-model="selectedSubcliente"
          :options="subclienteOptionsWithAll"
          track-by="value"
          label="label"
          placeholder="Todos os sub-clientes"
          @select="onSubclienteSelect"
        />
      </div>

      <div class="filter-group">
        <label class="filter-label">Local Instalação</label>
        <multiselect
          v-model="selectedLocal"
          :options="localInstalacaoOptionsWithAll"
          track-by="value"
          label="label"
          placeholder="Todas as instalações"
          @select="(opt) => updateFilter('local_instalacao_id', opt.value)"
        />
      </div>

      <!-- MULTI: Família -->
      <div class="filter-group">
        <label class="filter-label">Família</label>
        <multiselect
          v-model="selectedFamilia"
          :options="familiaOptions"
          track-by="value"
          label="label"
          placeholder="Todas as famílias"
          :multiple="true"
          :close-on-select="false"
          :clear-on-select="false"
          :searchable="true"
          @update:model-value="onFamiliaChange"
        >
          <template #noResult>Nenhuma família encontrada</template>
          <template #noOptions>Sem opções disponíveis</template>
        </multiselect>
      </div>

      <!-- MULTI: Técnico -->
      <div class="filter-group">
        <label class="filter-label">Técnico</label>
        <multiselect
          v-model="selectedTecnico"
          :options="tecnicoOptions"
          track-by="value"
          label="label"
          placeholder="Todos os técnicos"
          :multiple="true"
          :close-on-select="false"
          :clear-on-select="false"
          :searchable="true"
          @update:model-value="onTecnicoChange"
        >
          <template #noResult>Nenhum técnico encontrado</template>
          <template #noOptions>Sem opções disponíveis</template>
        </multiselect>
      </div>

      <!-- MULTI: Estado da Ordem -->
      <div class="filter-group">
        <label class="filter-label">Estado da Ordem</label>
        <multiselect
          v-model="selectedOsStatus"
          :options="ordemservicoStatusOptions"
          track-by="value"
          label="label"
          placeholder="Todos os estados"
          :multiple="true"
          :close-on-select="false"
          :clear-on-select="false"
          :searchable="false"
          @update:model-value="onOsStatusChange"
        >
          <template #noResult>Nenhum estado encontrado</template>
          <template #noOptions>Sem opções disponíveis</template>
        </multiselect>
      </div>

      

      <div class="filter-group">
        <label class="filter-label">Tipo de Relatório</label>
        <multiselect
          v-model="selectedTipoRelatorio"
          :options="tipoRelatorioOptionsWithAll"
          track-by="value"
          label="label"
          placeholder="Todos os tipos"
          @select="(opt) => updateFilter('tipo_relatorio_id', opt.value)"
        />
      </div>

      <!-- MULTI: Responsável -->
      <div class="filter-group">
        <label class="filter-label">Responsável</label>
        <multiselect
          v-model="selectedResponsavel"
          :options="responsavelOptions"
          track-by="value"
          label="label"
          placeholder="Todos os responsáveis"
          :multiple="true"
          :close-on-select="false"
          :clear-on-select="false"
          :searchable="true"
          @update:model-value="onResponsavelChange"
        >
          <template #noResult>Nenhum responsável encontrado</template>
          <template #noOptions>Sem opções disponíveis</template>
        </multiselect>
      </div>

      <div class="filter-group">
        <label class="filter-label">Fatura</label>
        <multiselect
          v-model="selectedFatura"
          :options="faturaOptions"
          track-by="value"
          label="label"
          placeholder="Todos"
          @select="(opt) => updateFilter('fatura', opt.value)"
        />
      </div>

      <!-- Pagamento Técnico (boolean) -->
      <div class="filter-group">
        <label class="filter-label">Pagamento Técnico</label>
        <multiselect
          v-model="selectedPagamentoTecnico"
          :options="pagamentoTecnicoOptions"
          track-by="value"
          label="label"
          placeholder="Todos"
          @select="(opt) => updateFilter('pagamento_tecnico', opt.value)"
        />
      </div>

      <!-- Tipo de Pagamento (multiselect) -->
      <div class="filter-group">
        <label class="filter-label">Tipo de Pagamento</label>
        <multiselect
          v-model="selectedTpPagamento"
          :options="tpPagamentoOptions"
          track-by="value"
          label="label"
          placeholder="Todos os tipos"
          :multiple="true"
          :close-on-select="false"
          :clear-on-select="false"
          :searchable="true"
          @update:model-value="onTpPagamentoChange"
        >
          <template #noResult>Nenhum tipo encontrado</template>
          <template #noOptions>Sem opções disponíveis</template>
        </multiselect>
      </div>

      <!-- Reorçamento (boolean) -->
      <div class="filter-group">
        <label class="filter-label">Reorçamento</label>
        <multiselect
          v-model="selectedReorcamentar"
          :options="reorcamentarOptions"
          track-by="value"
          label="label"
          placeholder="Todos"
          @select="(opt) => updateFilter('reorcamentar', opt.value)"
        />
      </div>

      <!-- Estado Reorçamento (multiselect) -->
      <div class="filter-group">
        <label class="filter-label">Estado Reorçamento</label>
        <multiselect
          v-model="selectedReorcamentoEstado"
          :options="reorcamentoEstadoOptions"
          track-by="value"
          label="label"
          placeholder="Todos os estados"
          :multiple="true"
          :close-on-select="false"
          :clear-on-select="false"
          @update:model-value="onReorcamentoEstadoChange"
        >
          <template #noResult>Nenhum estado encontrado</template>
          <template #noOptions>Sem opções disponíveis</template>
        </multiselect>
      </div>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
      <div class="filter-group">
        <label class="filter-label">ID da Ordem</label>
        <input v-model="filters.ordem_id_search" type="number" placeholder="Ex: 123" @input="onFilterChange" class="filter-input" />
      </div>
      <div class="filter-group">
        <label class="filter-label">ID do Negócio</label>
        <input v-model="filters.negocio_id_search" type="number" placeholder="Ex: 456" @input="onFilterChange" class="filter-input" />
      </div>
      <div class="filter-group">
        <label class="filter-label">Nº Fatura</label>
        <input v-model="filters.fatura_numero_search" type="text" placeholder="Ex: FT 001/2024" @input="onFilterChange" class="filter-input" />
      </div>
    </div>

    <div class="flex justify-end mt-6">
      <button @click="applyFilters" class="px-6 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 transition-colors focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
        Aplicar Filtros
      </button>
    </div>

    <div v-if="hasActiveFilters" class="mt-4 p-3 bg-blue-600 bg-opacity-20 rounded-md">
      <div class="text-sm text-blue-300">
        <strong>Filtros Ativos:</strong>
        <span v-for="(summary, index) in getFilterSummary()" :key="index" class="ml-2">
          {{ summary }}
        </span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import axios from "axios";
import { subDays, subWeeks, subMonths, format } from "date-fns";
import { DatePicker } from "v-calendar";
import Multiselect from "vue-multiselect";
import "v-calendar/style.css";
import "vue-multiselect/dist/vue-multiselect.css";
import { getOrdemServicoStatusLabel, normalizeOrdemServicoStatus } from "@/utils/ordemServicoStatusLabel";

const emit = defineEmits(["filtersChanged", "close-filter"]);

const STORAGE_KEY = "ordemServico_filters";

// --- 1. Filter State (For API) ---
const filters = ref({
  data_inicial_inicio: "",
  data_inicial_fim: "",
  cliente_id: "",
  subcliente_id: "",
  local_instalacao_id: "",
  familia_ids: [],
  utilizador_ids: [],
  ordemservico_status_in: [],
  include_relatorio_temporario: "",
  negocio_status: "",
  tipo_relatorio_id: "",
  responsavel_ids: [],
  fatura: "",
  pagamento_tecnico: "",
  tp_pagamento_ids: [],
  reorcamentar: "",
  reorcamento_estado_in: [],
  ordem_id_search: "",
  negocio_id_search: "",
  fatura_numero_search: "",
});

// --- 2. Options State ---
const clienteOptions = ref([]);
const subclienteOptions = ref([]);
const localInstalacaoOptions = ref([]);
const familiaOptions = ref([]);
const tecnicoOptions = ref([]);
const ordemservicoStatusOptions = ref([]);
const negocioStatusOptions = ref([]);
const tipoRelatorioOptions = ref([]);
const responsavelOptions = ref([]);

const datePresets = [
  { value: 'custom', label: 'Personalizado' },
  { value: 'today', label: 'Hoje' },
  { value: 'yesterday', label: 'Ontem' },
  { value: 'week', label: 'Última Semana' },
  { value: 'month', label: 'Último Mês' },
  { value: 'quarter', label: 'Último Trimestre' }
];

const faturaOptions = [
  { value: "", label: "Todos" },
  { value: "true", label: "Sim" },
  { value: "false", label: "Não" }
];

const pagamentoTecnicoOptions = [
  { value: "", label: "Todos" },
  { value: "true", label: "Sim" },
  { value: "false", label: "Não" }
];

const tpPagamentoOptions = ref([]);

const reorcamentarOptions = [
  { value: "", label: "Todos" },
  { value: "true", label: "Sim" },
  { value: "false", label: "Não" }
];

const reorcamentoEstadoOptions = ref([]);

// --- 3. UI Selection Objects ---
const selectedDataInicialPreset = ref({ value: 'month', label: 'Último Mês' });
const selectedCliente = ref({ value: "", label: "Todos os clientes" });
const selectedSubcliente = ref({ value: "", label: "Todos os sub-clientes" });
const selectedLocal = ref({ value: "", label: "Todas as instalações" });
const selectedFamilia = ref([]);      // multi
const selectedTecnico = ref([]);      // multi
const selectedOsStatus = ref([]);     // multi
const selectedNegocioStatus = ref({ value: "", label: "Todos os estados" });
const selectedTipoRelatorio = ref({ value: "", label: "Todos os tipos" });
const selectedResponsavel = ref([]);  // multi
const selectedFatura = ref({ value: "", label: "Todos" });
const selectedPagamentoTecnico = ref({ value: "", label: "Todos" });
const selectedTpPagamento = ref([]);
const selectedReorcamentar = ref({ value: "", label: "Todos" });
const selectedReorcamentoEstado = ref([]);

const dataInicialRange = ref({ start: null, end: null });
const dateMasks = { input: "DD/MM/YYYY" };

// --- 4. Computed Properties ---

const isCustomDate = computed(() => !selectedDataInicialPreset.value || selectedDataInicialPreset.value.value === 'custom');

const withAll = (arr, label = "Todos") => [{ value: "", label }, ...arr];

const clienteOptionsWithAll = computed(() => withAll(clienteOptions.value, "Todos os clientes"));

const subclienteOptionsWithAll = computed(() => {
  let opts = subclienteOptions.value;
  if (selectedCliente.value?.value) {
    opts = opts.filter(o => o.cliente_id === selectedCliente.value.value);
  }
  return withAll(opts, "Todos os sub-clientes");
});

const localInstalacaoOptionsWithAll = computed(() => {
  let opts = localInstalacaoOptions.value;
  if (selectedSubcliente.value?.value) {
    opts = opts.filter(o => o.subcliente_id === selectedSubcliente.value.value);
  } else if (selectedCliente.value?.value) {
    opts = opts.filter(o => o.cliente_id === selectedCliente.value.value);
  }
  return withAll(opts, "Todas as instalações");
});
const negocioStatusOptionsWithAll = computed(() => withAll(negocioStatusOptions.value, "Todos os estados"));
const tipoRelatorioOptionsWithAll = computed(() => withAll(tipoRelatorioOptions.value, "Todos os tipos"));

const hasActiveFilters = computed(() =>
  Object.entries(filters.value).some(([, v]) => {
    if (Array.isArray(v)) return v.length > 0;
    return v !== "" && v !== null && v !== undefined;
  })
);

// --- 5. Multi-select handlers ---

const onFamiliaChange = (selected) => {
  filters.value.familia_ids = selected.map(s => s.value);
  onFilterChange();
};

const onTecnicoChange = (selected) => {
  filters.value.utilizador_ids = selected.map(s => s.value);
  onFilterChange();
};

const onOsStatusChange = (selected) => {
  filters.value.ordemservico_status_in = selected.map(s => s.value);
  filters.value.include_relatorio_temporario = selected.some(s => s.value === 'execução') ? 'true' : '';
  onFilterChange();
};

const onResponsavelChange = (selected) => {
  filters.value.responsavel_ids = selected.map(s => s.value);
  onFilterChange();
};

const onTpPagamentoChange = (selected) => {
  filters.value.tp_pagamento_ids = selected.map(s => s.value);
  onFilterChange();
};

const onReorcamentoEstadoChange = (selected) => {
  filters.value.reorcamento_estado_in = selected.map(s => s.value);
  onFilterChange();
};

// --- 6. Cascading cliente/subcliente handlers ---

const onClienteSelect = (opt) => {
  filters.value.cliente_id = opt.value;
  if (opt.value) {
    if (selectedSubcliente.value?.value) {
      const valid = subclienteOptions.value.some(
        sc => sc.value === selectedSubcliente.value.value && sc.cliente_id === opt.value
      );
      if (!valid) {
        selectedSubcliente.value = { value: "", label: "Todos os sub-clientes" };
        filters.value.subcliente_id = "";
      }
    }
    if (selectedLocal.value?.value) {
      const valid = localInstalacaoOptions.value.some(
        li => li.value === selectedLocal.value.value && li.cliente_id === opt.value
      );
      if (!valid) {
        selectedLocal.value = { value: "", label: "Todas as instalações" };
        filters.value.local_instalacao_id = "";
      }
    }
  }
  onFilterChange();
};

const onSubclienteSelect = (opt) => {
  filters.value.subcliente_id = opt.value;
  if (opt.value && selectedLocal.value?.value) {
    const valid = localInstalacaoOptions.value.some(
      li => li.value === selectedLocal.value.value && li.subcliente_id === opt.value
    );
    if (!valid) {
      selectedLocal.value = { value: "", label: "Todas as instalações" };
      filters.value.local_instalacao_id = "";
    }
  }
  onFilterChange();
};

const updateFilter = (key, val) => {
  filters.value[key] = val;
  onFilterChange();
};

const handleDataPresetSelect = (opt) => {
  if (opt.value === 'custom') {
    filters.value.data_inicial_inicio = "";
    filters.value.data_inicial_fim = "";
    dataInicialRange.value = { start: null, end: null };
  } else {
    applyDataPreset(opt.value);
  }
};

const applyDataPreset = (p) => {
  const today = new Date();
  let start = today;
  if (p === 'yesterday') {
    start = subDays(today, 1);
    filters.value.data_inicial_inicio = filters.value.data_inicial_fim = format(start, 'yyyy-MM-dd');
  } else {
    if (p === 'today') start = today;
    else if (p === 'week') start = subWeeks(today, 1);
    else if (p === 'month') start = subMonths(today, 1);
    else if (p === 'quarter') start = subMonths(today, 3);
    filters.value.data_inicial_inicio = format(start, 'yyyy-MM-dd');
    filters.value.data_inicial_fim = format(today, 'yyyy-MM-dd');
  }
  dataInicialRange.value = { start, end: p === 'yesterday' ? start : today };
  onFilterChange();
};

const onDataInicialRangeChange = (range) => {
  if (range?.start && range?.end) {
    filters.value.data_inicial_inicio = format(range.start, 'yyyy-MM-dd');
    filters.value.data_inicial_fim = format(range.end, 'yyyy-MM-dd');
    onFilterChange();
  }
};

const onFilterChange = () => {
  clearTimeout(window.osFilterTimeout);
  window.osFilterTimeout = setTimeout(() => applyFilters(), 500);
};

const saveToStorage = () => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({
    filters: filters.value,
    selectedDataInicialPreset: selectedDataInicialPreset.value,
    selectedCliente: selectedCliente.value,
    selectedSubcliente: selectedSubcliente.value,
    selectedLocal: selectedLocal.value,
    selectedFamilia: selectedFamilia.value,
    selectedTecnico: selectedTecnico.value,
    selectedOsStatus: selectedOsStatus.value,
    selectedNegocioStatus: selectedNegocioStatus.value,
    selectedTipoRelatorio: selectedTipoRelatorio.value,
    selectedResponsavel: selectedResponsavel.value,
    selectedFatura: selectedFatura.value,
    selectedPagamentoTecnico: selectedPagamentoTecnico.value,
    selectedTpPagamento: selectedTpPagamento.value,
    selectedReorcamentar: selectedReorcamentar.value,
    selectedReorcamentoEstado: selectedReorcamentoEstado.value,
    dataInicialRange: dataInicialRange.value,
  }));
};

const applyFilters = () => {
  const active = {};

  for (const [key, val] of Object.entries(filters.value)) {
    if (Array.isArray(val)) {
      if (val.length > 0) active[key] = val.join(',');
    } else if (val !== "" && val !== null && val !== undefined) {
      active[key] = val;
    }
  }

  saveToStorage();
  emit("filtersChanged", active);
};

const loadMetadata = async () => {
  try {
    const { data } = await axios.get(`${process.env.VUE_APP_URL_BASE}/api/v1/ordemservico/metadata/`);
    const metadataFilters = data.data.filters;

    clienteOptions.value = metadataFilters.find(i => i.key === 'cliente_id')?.options || [];
    subclienteOptions.value = metadataFilters.find(i => i.key === 'subcliente_id')?.options || [];
    localInstalacaoOptions.value = metadataFilters.find(i => i.key === 'local_instalacao_id')?.options || [];
    familiaOptions.value = metadataFilters.find(i => i.key === 'familia_id')?.options || [];
    tecnicoOptions.value = metadataFilters.find(i => i.key === 'utilizador_id')?.options || [];
    ordemservicoStatusOptions.value = (metadataFilters.find(i => i.key === 'ordemservico_status')?.options || [])
      .map(opt => ({
        value: normalizeOrdemServicoStatus(opt.value),
        label: getOrdemServicoStatusLabel(opt.value),
      }))
      .filter((opt, idx, arr) => arr.findIndex(o => o.value === opt.value) === idx);
    negocioStatusOptions.value = metadataFilters.find(i => i.key === 'negocio_status')?.options || [];
    tipoRelatorioOptions.value = metadataFilters.find(i => i.key === 'tipo_relatorio_id')?.options || [];
    responsavelOptions.value = metadataFilters.find(i => i.key === 'responsavel_id')?.options || [];
    tpPagamentoOptions.value = metadataFilters.find(i => i.key === 'tp_pagamento_id')?.options || [];
    reorcamentoEstadoOptions.value = metadataFilters.find(i => i.key === 'reorcamento_estado')?.options || [];

    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const state = JSON.parse(saved);
        filters.value = { ...filters.value, ...state.filters };
        selectedDataInicialPreset.value = state.selectedDataInicialPreset;
        selectedCliente.value = state.selectedCliente;
        selectedSubcliente.value = state.selectedSubcliente;
        selectedLocal.value = state.selectedLocal;
        selectedFamilia.value = Array.isArray(state.selectedFamilia) ? state.selectedFamilia : [];
        selectedTecnico.value = Array.isArray(state.selectedTecnico) ? state.selectedTecnico : [];
        selectedOsStatus.value = Array.isArray(state.selectedOsStatus) ? state.selectedOsStatus : [];
        selectedNegocioStatus.value = state.selectedNegocioStatus;
        selectedTipoRelatorio.value = state.selectedTipoRelatorio;
        selectedResponsavel.value = Array.isArray(state.selectedResponsavel) ? state.selectedResponsavel : [];
        selectedFatura.value = state.selectedFatura;
        selectedPagamentoTecnico.value = state.selectedPagamentoTecnico || { value: "", label: "Todos" };
        selectedTpPagamento.value = Array.isArray(state.selectedTpPagamento) ? state.selectedTpPagamento : [];
        selectedReorcamentar.value = state.selectedReorcamentar || { value: "", label: "Todos" };
        selectedReorcamentoEstado.value = Array.isArray(state.selectedReorcamentoEstado) ? state.selectedReorcamentoEstado : [];
        dataInicialRange.value = {
          start: state.dataInicialRange?.start ? new Date(state.dataInicialRange.start) : null,
          end: state.dataInicialRange?.end ? new Date(state.dataInicialRange.end) : null,
        };
        applyFilters();
      } catch {
        localStorage.removeItem(STORAGE_KEY);
        applyDataPreset('month');
      }
    } else {
      applyDataPreset('month');
    }
  } catch (e) {
    console.error("Error loading metadata:", e);
  }
};

const clearFilters = () => {
  filters.value = {
    data_inicial_inicio: "", data_inicial_fim: "",
    cliente_id: "", subcliente_id: "", local_instalacao_id: "",
    familia_ids: [], utilizador_ids: [], ordemservico_status_in: [],
    include_relatorio_temporario: "",
    negocio_status: "", tipo_relatorio_id: "", responsavel_ids: [],
    fatura: "", pagamento_tecnico: "", tp_pagamento_ids: [],
    reorcamentar: "", reorcamento_estado_in: [],
    ordem_id_search: "", negocio_id_search: "", fatura_numero_search: "",
  };

  selectedCliente.value = { value: "", label: "Todos os clientes" };
  selectedSubcliente.value = { value: "", label: "Todos os sub-clientes" };
  selectedLocal.value = { value: "", label: "Todas as instalações" };
  selectedFamilia.value = [];
  selectedTecnico.value = [];
  selectedOsStatus.value = [];
  selectedNegocioStatus.value = { value: "", label: "Todos os estados" };
  selectedTipoRelatorio.value = { value: "", label: "Todos os tipos" };
  selectedResponsavel.value = [];
  selectedFatura.value = { value: "", label: "Todos" };
  selectedPagamentoTecnico.value = { value: "", label: "Todos" };
  selectedTpPagamento.value = [];
  selectedReorcamentar.value = { value: "", label: "Todos" };
  selectedReorcamentoEstado.value = [];

  selectedDataInicialPreset.value = { value: 'month', label: 'Último Mês' };
  localStorage.removeItem(STORAGE_KEY);
  applyDataPreset('month');
};

const getFilterSummary = () => {
  const summary = [];

  if (filters.value.data_inicial_inicio && filters.value.data_inicial_fim) {
    if (selectedDataInicialPreset.value?.value && selectedDataInicialPreset.value.value !== "custom") {
      summary.push(`Data: ${selectedDataInicialPreset.value.label}`);
    } else {
      summary.push(`Data: ${formatDisplayDate(filters.value.data_inicial_inicio)} - ${formatDisplayDate(filters.value.data_inicial_fim)}`);
    }
  }

  if (filters.value.cliente_id) summary.push(`Cliente: ${selectedCliente.value?.label || filters.value.cliente_id}`);
  if (filters.value.subcliente_id) summary.push(`Sub-Cliente: ${selectedSubcliente.value?.label || filters.value.subcliente_id}`);
  if (filters.value.local_instalacao_id) summary.push(`Local: ${selectedLocal.value?.label || filters.value.local_instalacao_id}`);

  if (selectedFamilia.value.length === 1) summary.push(`Família: ${selectedFamilia.value[0].label}`);
  else if (selectedFamilia.value.length > 1) summary.push(`Família: ${selectedFamilia.value.length} selecionadas`);

  if (selectedTecnico.value.length === 1) summary.push(`Técnico: ${selectedTecnico.value[0].label}`);
  else if (selectedTecnico.value.length > 1) summary.push(`Técnico: ${selectedTecnico.value.length} selecionados`);

  if (selectedOsStatus.value.length === 1) summary.push(`Estado OS: ${selectedOsStatus.value[0].label}`);
  else if (selectedOsStatus.value.length > 1) summary.push(`Estado OS: ${selectedOsStatus.value.length} selecionados`);

  if (filters.value.negocio_status) summary.push(`Estado Neg.: ${selectedNegocioStatus.value?.label || filters.value.negocio_status}`);
  if (filters.value.tipo_relatorio_id) summary.push(`Tipo Rel.: ${selectedTipoRelatorio.value?.label || filters.value.tipo_relatorio_id}`);

  if (selectedResponsavel.value.length === 1) summary.push(`Responsável: ${selectedResponsavel.value[0].label}`);
  else if (selectedResponsavel.value.length > 1) summary.push(`Responsável: ${selectedResponsavel.value.length} selecionados`);

  if (filters.value.fatura) summary.push(`Fatura: ${selectedFatura.value?.label || filters.value.fatura}`);
  if (filters.value.pagamento_tecnico) summary.push(`Pag. Técnico: ${selectedPagamentoTecnico.value?.label || filters.value.pagamento_tecnico}`);
  if (selectedTpPagamento.value.length === 1) summary.push(`Tp. Pagamento: ${selectedTpPagamento.value[0].label}`);
  else if (selectedTpPagamento.value.length > 1) summary.push(`Tp. Pagamento: ${selectedTpPagamento.value.length} selecionados`);
  if (filters.value.reorcamentar) summary.push(`Reorçamento: ${selectedReorcamentar.value?.label || filters.value.reorcamentar}`);
  if (selectedReorcamentoEstado.value.length === 1) summary.push(`Estado Reorç.: ${selectedReorcamentoEstado.value[0].label}`);
  else if (selectedReorcamentoEstado.value.length > 1) summary.push(`Estado Reorç.: ${selectedReorcamentoEstado.value.length} selecionados`);
  if (filters.value.ordem_id_search) summary.push(`OS #${filters.value.ordem_id_search}`);
  if (filters.value.negocio_id_search) summary.push(`Negócio #${filters.value.negocio_id_search}`);
  if (filters.value.fatura_numero_search) summary.push(`Nº Fatura: ${filters.value.fatura_numero_search}`);
  return summary;
};

const formatDisplayDate = (d) => d ? format(new Date(d), "dd/MM/yyyy") : "";
const closeFilter = () => emit("close-filter");

onMounted(loadMetadata);
</script>

<style scoped>
.filter-group {
  @apply flex flex-col space-y-1;
}

.filter-label {
  @apply text-sm font-medium text-gray-300;
}

.filter-input {
  @apply bg-[var(--fundoForm)] border border-gray-600 rounded px-3 py-2 text-[var(--text)] text-sm;
  @apply focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500;
}

.filter-input:focus {
  @apply bg-opacity-80;
}

.ordemservico-filter-container {
  @apply border border-gray-700;
}

:deep(.multiselect) {
  @apply bg-[var(--fundoForm)] border border-gray-600 rounded text-[var(--text)] text-sm;
  min-height: 38px;
}

:deep(.multiselect__tags) {
  @apply bg-[var(--fundoForm)] border-none;
  min-height: 38px;
  padding: 4px 40px 4px 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

:deep(.multiselect__input) {
  @apply bg-transparent text-[var(--text)];
  margin: 0;
  padding: 0;
}

:deep(.multiselect__placeholder) {
  @apply text-gray-400;
  margin-bottom: 8px;
  padding-top: 0;
}

:deep(.multiselect__single) {
  @apply bg-transparent text-[var(--text)];
  margin-bottom: 8px;
}

:deep(.multiselect__select) {
  height: 38px;
}

:deep(.multiselect__content-wrapper) {
  @apply bg-[var(--fundoComponente)] border border-gray-600;
}

:deep(.multiselect__content) {
  @apply bg-[var(--fundoForm)];
}

:deep(.multiselect__element) {
  @apply bg-[var(--fundoForm)];
}

:deep(.multiselect__option) {
  @apply text-gray-300;
  padding: 12px;
  min-height: 40px;
}

:deep(.multiselect__option--highlight) {
  @apply bg-blue-600 text-white;
}

:deep(.multiselect__option--selected) {
  @apply bg-blue-700 text-white font-medium;
}

:deep(.multiselect__option--selected.multiselect__option--highlight) {
  @apply bg-blue-600 text-white;
}

/* Tags for multi-select */
:deep(.multiselect__tag) {
  background: rgba(59, 130, 246, 0.25);
  border: 1px solid rgba(59, 130, 246, 0.5);
  color: #93c5fd;
  border-radius: 6px;
  padding: 2px 24px 2px 8px;
  font-size: 11px;
  margin: 0;
}

:deep(.multiselect__tag-icon) {
  border-radius: 0 6px 6px 0;
}

:deep(.multiselect__tag-icon::after) {
  color: #93c5fd;
}

:deep(.multiselect__tag-icon:hover) {
  background: rgba(239, 68, 68, 0.3);
}

:deep(.multiselect__tag-icon:hover::after) {
  color: #fca5a5;
}
</style>
